import { useState, useEffect, FormEvent } from 'react';
import { useNavigate, useLocation, createSearchParams } from "react-router-dom";
import { useRecoilState } from "recoil";

import styled, { keyframes } from 'styled-components';
import { 
  UserOutlined, 
  LogoutOutlined, 
  UserAddOutlined, 
  ExclamationCircleOutlined,
  CloseOutlined,
  DownOutlined,
  UpOutlined,
  GlobalOutlined
} from "@ant-design/icons";

import { ACCESS_TOKEN } from "../../lib/cookie";
import { navBarVisibleAtom } from "../../atom";
import { checkUser } from '../../api/authAPI';
import { getBoardListWithRootCategory } from '../../api/categoryAPI';
import { MainColor } from '../../style/styleValue';
import { Lang, useLangState } from '../../i18n';

// 카테고리 권한 관리 대상 게시판(= 권한이 하나도 없으면 메뉴에서 숨김) 의 백엔드 board.name.
// 갤러리/진단오류제보/Q&A/자료실 등은 카테고리 권한 대상이 아니므로 여기에 포함하지 않는다.
const BOARD_NAME = {
  organ: 'Organ',                       // 질환 카테고리
  conference: '소아병리연구회 집담회',
  studentPractice: '학생 병원 병리학 실습',
  consultation: 'Consultation',
  electronMicroscope: '전자현미경 연구회',
  lecture: '학술강좌',
  placenta: 'Placenta',
  muscle: 'Muscle',
  research: 'Research',
} as const;

const ko = {
  about: 'Bepath 소개',
  mission: '미션과 비전',
  overview: '아틀라스 개요',
  committee: '운영진 및 자문위원',
  encouragement: '격려의 글',
  tribute: '기리는 분들',
  gallery: '아틀라스 갤러리',
  portal: '질환 카테고리',
  members: '회원공간',
  conference: '소아병리연구회 집담회',
  studentPractice: '학생 병원 병리학 실습',
  electronMicroscope: '전자현미경 연구회',
  lecture: '학술강좌',
  reference: '자료실 및 게시판',
  publication: 'Publication',
  admin: '관리자',
  mypage: '마이페이지',
  login: '로그인',
  logout: '로그아웃',
  signup: '회원가입',
  search: '검색',
  language: '언어',
  confirmLogout: '로그아웃 하시겠습니까?',
  searchTooShort: '검색어는 공백을 제외하고 최소 두 글자 이상이어야 합니다.',
  membersOnly: (menu: string) => `${menu} 페이지는 회원 전용 공간입니다. 로그인 후 이용해주세요.`,
  notForOther: (menu: string) => `${menu} 페이지는 기타회원은 접근할 수 없는 공간입니다.`,
  notForStudent: '소아병리연구회 집담회는 학생회원은 접근할 수 없는 공간입니다.',
};

const en: typeof ko = {
  about: 'About BePath',
  mission: 'Mission & Vision',
  overview: 'Atlas Overview',
  committee: 'Committee & Advisors',
  encouragement: 'Messages of Encouragement',
  tribute: 'In Memoriam',
  gallery: 'Atlas Gallery',
  portal: 'Disease Categories',
  members: 'Members',
  conference: 'Pediatric Pathology Study Group Conference',
  studentPractice: 'Student Pathology Practicum',
  electronMicroscope: 'Electron Microscopy Study Group',
  lecture: 'Academic Lectures',
  reference: 'Resources & Board',
  publication: 'Publication',
  admin: 'Admin',
  mypage: 'My Page',
  login: 'Log in',
  logout: 'Log out',
  signup: 'Sign up',
  search: 'Search',
  language: 'Language',
  confirmLogout: 'Do you want to log out?',
  searchTooShort: 'Please enter at least two characters, excluding spaces.',
  membersOnly: (menu: string) => `${menu} is for members only. Please log in to continue.`,
  notForOther: (menu: string) => `${menu} is not available to Other members.`,
  notForStudent: 'The Pediatric Pathology Study Group Conference is not available to Student members.',
};

const MESSAGES = { ko, en };

const LANGUAGE_OPTIONS: { value: Lang; label: string }[] = [
  { value: 'ko', label: '한국어' },
  { value: 'en', label: 'EN' },
];

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [keyword, setKeyword] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [isHovered, setIsHovered] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [mobileSubMenuOpen, setMobileSubMenuOpen] = useState<{ [key: string]: boolean }>({});
  const [accessibleBoards, setAccessibleBoards] = useState<Set<string>>(new Set());
  const [lang, setLang] = useLangState();
  const t = MESSAGES[lang];

  // 접근 권한(게시판 역할)이 있는 게시판만 노출. 기본값은 '숨김'이라 비로그인/미로드
  // 상태에서는 권한 대상 게시판이 나타나지 않고, 권한이 확인된 게시판만 표시된다.
  const canSeeBoard = (boardName: string) => accessibleBoards.has(boardName);

  const isPostPage = location.pathname.startsWith('/post/');
  const isDemoConsultationPage = location.pathname.startsWith('/demo/consultation');

  const handleAuthAction = () => {
    if (isLoggedIn) {
      if (window.confirm(t.confirmLogout)) {
        localStorage.removeItem(ACCESS_TOKEN);
        setIsLoggedIn(false);
        setIsAdmin(false);
        navigate("/");
      }
    } else {
      navigate("/login");
    }
  };

  const handleSearch = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const trimmedValue = keyword.trim();

    if (trimmedValue.length < 2) {
      alert(t.searchTooShort);
      return;
    }

    navigate({
      pathname: "/search",
      search: `?${createSearchParams({ title: trimmedValue })}`,
    });
    setIsMobileOpen(false);
  };

  const toggleMobileSubMenu = (menuName: string) => {
    setMobileSubMenuOpen(prev => ({
      ...prev,
      [menuName]: !prev[menuName]
    }));
  };

  const [, setNavBarVisible] = useRecoilState(navBarVisibleAtom);
  // const shouldHideNavBar = isDemoConsultationPage || (isPostPage && !isHovered);
  const shouldHideNavBar = isDemoConsultationPage;

  // menuName 은 dedicated 게시판 이동 시 board.name 으로 쓰인다. 안내 문구에는 label(현재 언어)을 쓴다
  const clickMenu = (menuName: string, path: string, label: string = menuName) => {
    // 로그인 체크
    if (!isLoggedIn) {
      alert(t.membersOnly(label));
      return;
    }

    // 권한 체크: 기타회원(OTHER) 접근 제한
    const restrictedForOther = [
      "gallery", "portal", "dedicated", "conference", 
      "report", "qna", "reference"
    ];
    if (userRole === "OTHER" && restrictedForOther.includes(path)) {
      alert(t.notForOther(label));
      return;
    }

    // 권한 체크: 학생회원(STUDENT) 접근 제한
    if (userRole === "STUDENT" && path === "conference") {
      alert(t.notForStudent);
      return;
    }

    if (path === "dedicated") {
      navigate(`/${path}?board=${menuName}`);
    } else {
      navigate(`/${path}`);
    }
  };

  useEffect(() => {
    const checkLoginStatus = async () => {
      const token = localStorage.getItem(ACCESS_TOKEN);
      const hasToken = !!token;
      setIsLoggedIn(hasToken);
      if (hasToken) {
        const userRole = await checkUser();
        setIsAdmin(userRole === "ADMIN_MAIN");
        setUserRole(userRole);

        // 접근 가능한 게시판 목록(서버에서 유저 권한 기준으로 필터링됨)을 받아 메뉴 노출에 사용
        try {
          const boards = await getBoardListWithRootCategory();
          setAccessibleBoards(new Set<string>((boards || []).map((b: any) => b.name)));
        } catch (e) {
          console.error("게시판 권한 목록을 불러오지 못했습니다:", e);
          setAccessibleBoards(new Set());
        }
      } else {
        setIsAdmin(false);
        setAccessibleBoards(new Set());
      }
    };
    checkLoginStatus();
  }, [location.pathname]);

  useEffect(() => {
    setIsHovered(false);
    setIsMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    setNavBarVisible(!shouldHideNavBar);
  }, [shouldHideNavBar, setNavBarVisible]);

  useEffect(() => {
    if (isMobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    return () => { document.body.style.overflow = 'auto'; };
  }, [isMobileOpen]);

  return (
    <>
      <FixedWrapper
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {!isDemoConsultationPage && (
        <NavBar $isHidden={shouldHideNavBar}>
          <HeaderInner>
            <LogoWrap onClick={() => navigate('/')}>
              <img src="/img/logo/ATLASLOGO1.png" alt="ATLAS" />
            </LogoWrap>

            <RightGroup>
              <Navigation>
                <MainUl>
                  <MainLi>
                    <MenuLink as="div">
                      {t.about} <i className="arrow" />
                    </MenuLink>
                    <SubMenuUl style={lang === 'en' ? { width: "260px" } : undefined}>
                      <li onClick={() => navigate('/mission')}>{t.mission}</li>
                      <li onClick={() => navigate('/')}>{t.overview}</li>
                      <li onClick={() => navigate('/committee')}>{t.committee}</li>
                      <li onClick={() => navigate('/encouragement')}>{t.encouragement}</li>
                      <li onClick={() => navigate('/tribute')}>{t.tribute}</li>
                    </SubMenuUl>
                  </MainLi>

                  <MainLi>
                    <MenuLink as="div" onClick={() => clickMenu('아틀라스 갤러리', 'gallery', t.gallery)}>
                      {t.gallery}
                    </MenuLink>
                  </MainLi>
                  {canSeeBoard(BOARD_NAME.organ) && (
                  <MainLi>
                    <MenuLink as="div" onClick={() => clickMenu('질환 카테고리', 'portal', t.portal)}>
                      {t.portal}
                    </MenuLink>
                  </MainLi>
                  )}

                  <MainLi>
                    <MenuLink as="div" onClick={() => navigate('/publication')}>
                      {t.publication}
                    </MenuLink>
                  </MainLi>

                  <MainLi>
                    <MenuLink as="div">
                      {t.members} <i className="arrow" />
                    </MenuLink>
                    <SubMenuUl style={{ width: lang === 'en' ? "380px" : "250px" }}>
                      {canSeeBoard(BOARD_NAME.conference) && <li onClick={() => clickMenu('소아병리연구회 집담회', 'conference', t.conference)}>{t.conference}</li>}
                      {canSeeBoard(BOARD_NAME.studentPractice) && <li onClick={() => clickMenu('학생 병원 병리학 실습', 'dedicated', t.studentPractice)}>{t.studentPractice}</li>}
                      {canSeeBoard(BOARD_NAME.consultation) && <li onClick={() => clickMenu('Consultation', 'dedicated')}>Consultation/Discussion</li>}
                      {canSeeBoard(BOARD_NAME.electronMicroscope) && <li onClick={() => clickMenu('전자현미경 연구회', 'dedicated', t.electronMicroscope)}>{t.electronMicroscope}</li>}
                      {canSeeBoard(BOARD_NAME.lecture) && <li onClick={() => clickMenu('학술강좌', 'dedicated', t.lecture)}>{t.lecture}</li>}
                      {/* <li onClick={() => clickMenu('진단오류제보게시판', 'report')}>진단오류제보게시판</li> */}
                      <li onClick={() => clickMenu('Q&A', 'qna')}>Q&A</li>
                      <li onClick={() => clickMenu('자료실 및 게시판', 'reference', t.reference)}>{t.reference}</li>
                    </SubMenuUl>
                  </MainLi>

                  {canSeeBoard(BOARD_NAME.placenta) && (
                  <MainLi>
                    <MenuLink as="div" onClick={() => clickMenu('Placenta', 'dedicated')}>
                      Placenta
                    </MenuLink>
                  </MainLi>
                  )}
                  {canSeeBoard(BOARD_NAME.muscle) && (
                  <MainLi>
                    <MenuLink as="div" onClick={() => clickMenu('Muscle', 'dedicated')}>
                      Muscle
                    </MenuLink>
                  </MainLi>
                  )}
                  {canSeeBoard(BOARD_NAME.research) && (
                  <MainLi>
                    <MenuLink as="div" onClick={() => clickMenu('Research', 'dedicated')}>
                      Research
                    </MenuLink>
                  </MainLi>
                  )}
                </MainUl>
              </Navigation>

              <SearchWrapper onSubmit={handleSearch}>
                <SearchInput 
                  type="text" 
                  placeholder="Search diseases..." 
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                />
                <SearchBtn type="submit" aria-label={t.search}>
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M15.7955 15.8111L21 21M18 10.5C18 14.6421 14.6421 18 10.5 18C6.35786 18 3 14.6421 3 10.5C3 6.35786 6.35786 3 10.5 3C14.6421 3 18 6.35786 18 10.5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </SearchBtn>
              </SearchWrapper>

              <AuthGroup>
                {isAdmin && (
                  <AuthBtn onClick={() => navigate('/admin/category-editor')}>
                    <ExclamationCircleOutlined /> {t.admin}
                  </AuthBtn>
                )}
                {isLoggedIn && (
                  <AuthBtn onClick={() => navigate('/mypage')}>
                    <UserOutlined /> {t.mypage}
                  </AuthBtn>
                )}
                {isLoggedIn ? (
                  <AuthBtn onClick={handleAuthAction}>
                    <LogoutOutlined /> {t.logout}
                  </AuthBtn>
                ) : (
                  <>
                    <AuthBtn onClick={handleAuthAction}>
                      <UserOutlined /> {t.login}
                    </AuthBtn>
                    <AuthBtn $primary onClick={() => navigate('/signup')}>
                      <UserAddOutlined /> {t.signup}
                    </AuthBtn>
                  </>
                )}
                <LangToggle role="group" aria-label={t.language}>
                  <GlobalOutlined className="globe" />
                  {LANGUAGE_OPTIONS.map(option => (
                    <LangOption
                      key={option.value}
                      type="button"
                      $active={lang === option.value}
                      aria-pressed={lang === option.value}
                      onClick={() => setLang(option.value)}
                    >
                      {option.label}
                    </LangOption>
                  ))}
                </LangToggle>
              </AuthGroup>
            </RightGroup>

            <MobileToggle onClick={() => setIsMobileOpen(true)}>
              <span />
              <span />
              <span />
            </MobileToggle>
          </HeaderInner>
        </NavBar>
        )}
      </FixedWrapper>

      <MobileOverlay $isOpen={isMobileOpen} onClick={() => setIsMobileOpen(false)} />
      <MobileDrawer $isOpen={isMobileOpen}>
        <DrawerHeader>
          <img src="/img/logo/ATLASLOGO1.png" alt="ATLAS" style={{ height: '40px' }} />
          <CloseBtn onClick={() => setIsMobileOpen(false)}>
            <CloseOutlined style={{ fontSize: '24px' }} />
          </CloseBtn>
        </DrawerHeader>

        <MobileSearch onSubmit={handleSearch}>
          <input 
            type="text" 
            placeholder="Search diseases..."
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
          />
          <button type="submit"><SearchIcon /></button>
        </MobileSearch>

        <MobileNavList>
          <MobileItem>
            <MobileLink onClick={() => toggleMobileSubMenu('about')}>
              {t.about}
              {mobileSubMenuOpen['about'] ? <UpOutlined /> : <DownOutlined />}
            </MobileLink>
            <MobileSubMenu $isOpen={mobileSubMenuOpen['about'] || false}>
              <li onClick={() => navigate('/mission')}>{t.mission}</li>
              <li onClick={() => navigate('/')}>{t.overview}</li>
              <li onClick={() => navigate('/committee')}>{t.committee}</li>
              <li onClick={() => navigate('/encouragement')}>{t.encouragement}</li>
              <li onClick={() => navigate('/tribute')}>{t.tribute}</li>
            </MobileSubMenu>
          </MobileItem>
          <MobileItem>
            <MobileLink onClick={() => navigate('/gallery')}>{t.gallery}</MobileLink>
          </MobileItem>
          {canSeeBoard(BOARD_NAME.organ) && (
          <MobileItem>
            <MobileLink onClick={() => navigate('/portal')}>{t.portal}</MobileLink>
          </MobileItem>
          )}
          <MobileItem>
            <MobileLink onClick={() => navigate('/publication')}>{t.publication}</MobileLink>
          </MobileItem>
          <MobileItem>
            <MobileLink onClick={() => toggleMobileSubMenu('member')}>
              {t.members}
              {mobileSubMenuOpen['member'] ? <UpOutlined /> : <DownOutlined />}
            </MobileLink>
            <MobileSubMenu $isOpen={mobileSubMenuOpen['member'] || false}>
              {canSeeBoard(BOARD_NAME.conference) && <li onClick={() => navigate('/conference')}>{t.conference}</li>}
              {canSeeBoard(BOARD_NAME.studentPractice) && <li onClick={() => clickMenu('학생 병원 병리학 실습', 'dedicated', t.studentPractice)}>{t.studentPractice}</li>}
              {canSeeBoard(BOARD_NAME.consultation) && <li onClick={() => clickMenu('Consultation', 'dedicated')}>Consultation/Discussion</li>}
              {canSeeBoard(BOARD_NAME.electronMicroscope) && <li onClick={() => clickMenu('전자현미경 연구회', 'dedicated', t.electronMicroscope)}>{t.electronMicroscope}</li>}
              {canSeeBoard(BOARD_NAME.lecture) && <li onClick={() => clickMenu('학술강좌', 'dedicated', t.lecture)}>{t.lecture}</li>}
              {/* <li onClick={() => clickMenu('진단오류제보게시판', 'report')}>진단오류제보게시판</li> */}
              <li onClick={() => clickMenu('Q&A', 'qna')}>Q&A</li>
              <li onClick={() => clickMenu('자료실 및 게시판', 'reference', t.reference)}>{t.reference}</li>
            </MobileSubMenu>
          </MobileItem>
          {canSeeBoard(BOARD_NAME.placenta) && (
          <MobileItem>
            <MobileLink onClick={() => clickMenu('Placenta', 'dedicated')}>Placenta</MobileLink>
          </MobileItem>
          )}
          {canSeeBoard(BOARD_NAME.muscle) && (
          <MobileItem>
            <MobileLink onClick={() => clickMenu('Muscle', 'dedicated')}>Muscle</MobileLink>
          </MobileItem>
          )}
          {canSeeBoard(BOARD_NAME.research) && (
          <MobileItem>
            <MobileLink onClick={() => clickMenu('Research', 'dedicated')}>Research</MobileLink>
          </MobileItem>
          )}
        </MobileNavList>
        
        <MobileAuth>
           <MobileLangRow>
             <span><GlobalOutlined /> {t.language}</span>
             <LangToggle role="group" aria-label={t.language}>
               {LANGUAGE_OPTIONS.map(option => (
                 <LangOption
                   key={option.value}
                   type="button"
                   $active={lang === option.value}
                   aria-pressed={lang === option.value}
                   onClick={() => setLang(option.value)}
                 >
                   {option.label}
                 </LangOption>
               ))}
             </LangToggle>
           </MobileLangRow>
           {isLoggedIn ? (
             <button onClick={handleAuthAction} className="logout">
               <LogoutOutlined /> {t.logout}
             </button>
           ) : (
             <div className="auth-buttons">
               <button onClick={() => navigate('/login')}><UserOutlined /> {t.login}</button>
               <button onClick={() => navigate('/signup')}><UserAddOutlined /> {t.signup}</button>
             </div>
           )}
        </MobileAuth>
      </MobileDrawer>
    </>
  );
};

const SearchIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8"></circle>
    <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
  </svg>
);

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const FixedWrapper = styled.header`
  position: fixed;
  top: 0;
  width: 100%;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  box-shadow: 0 2px 10px rgba(0,0,0,0.05);
`;

const AuthGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
`;

const AuthBtn = styled.button<{ $primary?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: ${({ $primary }) => ($primary ? MainColor : 'transparent')};
  border: 1px solid ${({ $primary }) => ($primary ? MainColor : 'transparent')};
  color: ${({ $primary }) => ($primary ? '#fff' : '#555')};
  font-size: 13px;
  white-space: nowrap;
  cursor: pointer;
  padding: 6px 12px;
  border-radius: 6px;
  transition: all 0.2s;

  &:hover {
    color: ${({ $primary }) => ($primary ? '#fff' : MainColor)};
    background: ${({ $primary }) => ($primary ? '#0f2c4d' : '#f0f5ff')};
    border-color: ${({ $primary }) => ($primary ? '#0f2c4d' : 'transparent')};
  }

  .anticon {
    font-size: 14px;
  }
`;

const LangToggle = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  margin-left: 4px;
  padding: 2px;
  border: 1px solid #e3e6ea;
  border-radius: 6px;
  background: #fff;

  .globe {
    color: #888;
    font-size: 13px;
    margin: 0 4px;
  }
`;

const LangOption = styled.button<{ $active: boolean }>`
  border: none;
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 12px;
  line-height: 1.2;
  white-space: nowrap;
  cursor: pointer;
  background: ${({ $active }) => ($active ? MainColor : 'transparent')};
  color: ${({ $active }) => ($active ? '#fff' : '#555')};
  font-weight: ${({ $active }) => ($active ? 600 : 400)};
  transition: background 0.2s, color 0.2s;

  &:hover {
    background: ${({ $active }) => ($active ? MainColor : '#f0f5ff')};
  }
`;

const MobileLangRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  font-size: 14px;
  color: #555;

  span {
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
`;

const NavBar = styled.div<{ $isHidden: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  background-color: rgba(255, 255, 255, 0.98);
  border-bottom: 1px solid #ddd;
  
  height: ${({ $isHidden }) => ($isHidden ? '0' : '60px')};
  opacity: ${({ $isHidden }) => ($isHidden ? '0' : '1')};
  visibility: ${({ $isHidden }) => ($isHidden ? 'hidden' : 'visible')};
  overflow: ${({ $isHidden }) => ($isHidden ? 'hidden' : 'visible')};
  
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
`;

const HeaderInner = styled.div`
  width: 100%;
  margin: 0 auto;
  padding: 0 40px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 100%;

  @media (max-width: 768px) {
    padding: 0 20px;
  }
`;

const LogoWrap = styled.div`
  flex-shrink: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 10px;
  max-width: 65%;

  img {
    height: 70px;
    width: auto;
    max-width: 100%;
    object-fit: contain;
    transition: all 0.3s ease;
  }
  
  .ccrdplogo {
    height: 85px;
  }

  @media (max-width: 1300px) {
    img { height: 50px; }
    .ccrdplogo { height: 65px; }
  }
  
  @media (max-width: 768px) {
    max-width: 80%;
    img { 
      height: auto; 
      max-height: 40px; 
    }
    .ccrdplogo { 
      height: auto; 
      max-height: 50px; 
    }
  }

  @media (max-width: 480px) {
    img { max-height: 30px; }
    .ccrdplogo { max-height: 40px; }
  }
`;

const RightGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;

  @media (max-width: 1200px) {
    display: none;
  }
`;

const Navigation = styled.nav`
  height: 100%;
`;

const MainUl = styled.ul`
  display: flex;
  list-style: none;
  margin: 0;
  padding: 0;
  height: 100%;
`;

const SubMenuUl = styled.ul`
  position: absolute;
  top: 45px;
  left: 30%;
  transform: translateX(-50%);
  background: #fff;
  min-width: 200px;
  padding: 15px 0;
  list-style: none;
  box-shadow: 0 10px 30px rgba(0,0,0,0.12);
  border-radius: 8px;
  opacity: 0;
  visibility: hidden;
  transition: all 0.3s ease;
  z-index: 100;

  li {
    display: block;
    padding: 10px 25px;
    color: #555;
    font-size: 16px;
    transition: all 0.2s;
    cursor: pointer;

    &:hover {
      background: #f8f9fa;
      color: #0050b3;
      padding-left: 30px;
    }
  }
`;

const MenuLink = styled.div`
  color: #333;
  font-size: 15px;
  padding: 10px;
  display: flex;
  align-items: center;
  position: relative;
  transition: color 0.3s;
  white-space: nowrap;
  cursor: pointer;

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 10px;
    right: 10px;
    height: 2px;
    background: #0050b3;
    transform: scaleX(0);
    transition: transform 0.3s ease;
  }

  .arrow {
    margin-left: 6px;
    border: solid #888;
    border-width: 0 2px 2px 0;
    display: inline-block;
    padding: 2.5px;
    transform: rotate(45deg);
    transition: all 0.3s;
    margin-bottom: 2px;
  }
`;

const MainLi = styled.li`
  position: relative;
  margin: 0 10px;
  display: flex;
  align-items: center;
  cursor: pointer;

  &:hover {
    ${MenuLink} {
      color: #0050b3;
      &::after { transform: scaleX(1); }
      .arrow { 
        transform: rotate(-135deg) translateY(-2px); 
        border-color: #0050b3; 
      }
    }
    ${SubMenuUl} {
      opacity: 1;
      visibility: visible;
      animation: ${fadeIn} 0.3s ease forwards;
    }
  }
`;

const SearchWrapper = styled.form`
  position: relative;
  display: flex;
  align-items: center;
  width: 240px;
  height: 40px;
  background-color: #f5f5f5;
  border-radius: 20px;
  padding: 0 15px;
  transition: all 0.3s ease;
  border: 1px solid transparent;

  &:focus-within {
    background-color: #fff;
    border-color: #0050b3;
    box-shadow: 0 0 0 3px rgba(0, 80, 179, 0.1);
    width: 280px;
  }
`;

const SearchInput = styled.input`
  flex: 1;
  border: none;
  background: transparent;
  outline: none;
  font-size: 14px;
  color: #333;
  padding-right: 10px;

  &::placeholder {
    font-size: 1rem;
    color: #777;
  }
`;

const SearchBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #666;
  transition: color 0.3s;

  &:hover {
    color: #0050b3;
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const MobileToggle = styled.div`
  display: none;
  flex-direction: column;
  gap: 5px;
  cursor: pointer;
  z-index: 10002;
  
  @media (max-width: 1200px) { 
    display: flex; 
  }
  
  span {
    width: 26px;
    height: 2px;
    background: #333;
    border-radius: 2px;
  }
`;

const MobileOverlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  background: rgba(0, 0, 0, 0.5);
  z-index: 11000;
  opacity: ${({ $isOpen }) => ($isOpen ? 1 : 0)};
  visibility: ${({ $isOpen }) => ($isOpen ? 'visible' : 'hidden')};
  transition: all 0.3s ease;
`;

const MobileDrawer = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  right: 0;
  width: 300px;
  height: 100vh;
  background: #fff;
  z-index: 11001;
  transform: translateX(${({ $isOpen }) => ($isOpen ? '0' : '100%')});
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: -2px 0 10px rgba(0,0,0,0.1);
  display: flex;
  flex-direction: column;
`;

const DrawerHeader = styled.div`
  padding: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #f0f0f0;
`;

const CloseBtn = styled.div`
  cursor: pointer;
  padding: 5px;
  color: #333;
  &:hover { color: #0050b3; }
`;

const MobileSearch = styled.form`
  margin: 20px;
  display: flex;
  align-items: center;
  background: #f5f5f5;
  border-radius: 8px;
  padding: 10px 15px;

  input {
    flex: 1;
    border: none;
    background: transparent;
    outline: none;
    font-size: 14px;
  }
  button {
    border: none;
    background: transparent;
    cursor: pointer;
    display: flex;
    align-items: center;
  }
`;

const MobileNavList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  flex: 1;
  overflow-y: auto;
`;

const MobileItem = styled.li`
  border-bottom: 1px solid #f5f5f5;
`;

const MobileLink = styled.div`
  padding: 15px 20px;
  font-size: 16px;
  font-weight: 500;
  color: #333;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;

  &:hover {
    background-color: #f9f9f9;
    color: #0050b3;
  }
`;

const MobileSubMenu = styled.ul<{ $isOpen: boolean }>`
  background-color: #f8f9fa;
  list-style: none;
  padding: 0;
  overflow: hidden;
  max-height: ${({ $isOpen }) => ($isOpen ? '500px' : '0')};
  transition: max-height 0.3s ease-in-out;

  li {
    padding: 12px 20px 12px 35px;
    font-size: 14px;
    color: #666;
    cursor: pointer;
    border-bottom: 1px solid #eee;

    &:last-child {
      border-bottom: none;
    }
    &:hover {
      color: #0050b3;
      background: #eee;
    }
  }
`;

const MobileAuth = styled.div`
  padding: 20px;
  border-top: 1px solid #f0f0f0;

  .auth-buttons {
    display: flex;
    gap: 10px;
    
    button {
      flex: 1;
      padding: 10px;
      border-radius: 6px;
      border: 1px solid #ddd;
      background: #fff;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 5px;
      font-size: 14px;

      &:hover {
        background: #f0f0f0;
        border-color: #bbb;
      }
    }
  }

  .logout {
    width: 100%;
    padding: 10px;
    border-radius: 6px;
    border: 1px solid #ddd;
    background: #fff;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 5px;
    
    &:hover { background: #f0f0f0; }
  }
`;

export default Header;
